/**
 * chat-call-handler.ts
 * 从 chat.ts 提取的通话决策处理逻辑
 * 包括：AI接受/拒绝通话、AI主动发起通话、AI挂断、通话转录
 */

import { applyCallModePrefix, createAssistantMessage } from './chat-helpers'

/**
 * 通话处理所需的上下文
 */
export interface CallHandlerContext {
  callStore: any
  contactsStore: any
  airiCardStore: any
  bgActivityStore: any
  messages: { value: any[] }
  notifyMessagesChanged: () => void
  triggerFullTextSpeech?: (text: string) => Promise<void>
  send: (text: string, options: any) => Promise<void>
  sendOptions: {
    providerId: string
    model: string
    chatProvider: string
    providerConfig?: any
  }
}

/**
 * 通话处理结果
 */
export interface CallHandlerResult {
  /** 是否应该提前 return（阻止消息显示在聊天中） */
  shouldReturn: boolean
}

function dispatchCallGreeting(greeting: string, callType: 'voice' | 'video', ctx: CallHandlerContext): void {
  ctx.callStore.addToTranscript({
    role: 'assistant',
    content: greeting,
    timestamp: Date.now(),
  })
  const greetingMessage = createAssistantMessage(greeting)
  applyCallModePrefix(greetingMessage, callType)
  ctx.messages.value.push(greetingMessage)
  ctx.notifyMessagesChanged()
  void ctx.triggerFullTextSpeech?.(greeting)
}

/**
 * 4️⃣ 处理 AI 接受/拒绝通话决策（仅在等待接听时）
 * @returns shouldReturn=true 表示调用方应 resolve() 并 return
 */
export function handleCallDecision(
  fullText: string,
  handleAICallDecision: (text: string) => any,
  ctx: CallHandlerContext,
): CallHandlerResult {
  const { callStore } = ctx

  if (!callStore.outgoingCallPending) {
    return { shouldReturn: false }
  }

  const decision = handleAICallDecision(fullText)

  if (decision?.accepted) {
    console.info('📞 AI接受了通话')
    const pendingCallType = callStore.callType || 'voice'
    const pendingCharacterId = callStore.callCharacterId || 'unknown'
    const pendingCharacterName = callStore.callCharacterName || 'AI'
    const pendingCharacterAvatar = callStore.callCharacterAvatar || undefined

    callStore.cancelOutgoingCall()
    callStore.startCall(
      pendingCallType,
      pendingCharacterId,
      pendingCharacterName,
      pendingCharacterAvatar,
      'user',
    )

    // 直接使用 AI 决策中附带的问候语，无需第二次 AI 请求
    const greeting = decision.greeting
    if (greeting) {
      console.info('📞 通话已接通，使用AI决策中的问候语:', greeting.substring(0, 50))
      dispatchCallGreeting(greeting, pendingCallType, ctx)
    }
    else {
      // 兜底：如果 AI 没有返回 greeting，再发一次请求
      console.info('📞 通话已接通，AI未提供问候语，发送请求获取开场白')
      setTimeout(async () => {
        const callTypeText = pendingCallType === 'voice' ? '语音' : '视频'
        ctx.messages.value.push({
          role: 'system',
          content: `[系统通知：你刚刚接通了用户的${callTypeText}通话请求。现在通话已建立，你应该先开口说第一句话。记住：你现在处于${callTypeText}通话中，请调用 speak_in_call 工具说你的第一句话，自然地打个招呼即可，不要过于正式。]`,
          timestamp: Date.now(),
          metadata: { type: 'call_system_prompt' },
        } as any)
        ctx.notifyMessagesChanged()

        try {
          await ctx.send('', {
            providerId: ctx.sendOptions.providerId,
            model: ctx.sendOptions.model,
            chatProvider: ctx.sendOptions.chatProvider,
            providerConfig: ctx.sendOptions.providerConfig,
            skipUserMessage: true,
          })
        }
        catch (error) {
          console.error('AI开场白失败:', error)
        }
      }, 500)
    }

    return { shouldReturn: true }
  }
  else if (decision?.accepted === false) {
    console.info('📞 AI拒绝了通话:', decision.reason)
    callStore.cancelOutgoingCall()
    // 拒绝理由会继续正常流程显示在聊天中
  }

  return { shouldReturn: false }
}

/**
 * 5️⃣ 处理 AI 主动发起通话（包括后台活动触发）
 * @returns shouldReturn=true 表示调用方应 resolve() 并 return
 */
export function handleAIInitiatedCall(
  fullText: string,
  detectCallIntent: (text: string) => any,
  ctx: CallHandlerContext,
  parsedCallIntent?: { type: 'voice' | 'video', greeting?: string },
): CallHandlerResult {
  const { callStore, contactsStore, airiCardStore, bgActivityStore } = ctx

  if (callStore.isCallActive || callStore.outgoingCallPending || callStore.incomingCallOffer) {
    return { shouldReturn: false }
  }

  // Tool-only Responses have no text body; only inspect actual prose for legacy intents.
  if (fullText.trim() && bgActivityStore.handleAIActionResponse(fullText)) {
    return { shouldReturn: true }
  }

  // 优先使用 processAIResponse 已解析的 callIntent，避免重复扫描原始文本
  const callIntent = parsedCallIntent || detectCallIntent(fullText)
  if (callIntent.type) {
    console.info(`🤖 AI发起${callIntent.type}通话`)
    const activeContact = contactsStore.activeContact
    const characterId = activeContact?.id
      || String(airiCardStore.activeCard?.metadata?.id || airiCardStore.activeCard?.name || 'AI')
    const characterName = activeContact?.name || airiCardStore.activeCard?.name || 'AI'
    const characterAvatar = activeContact?.avatar || '/BOT.jpg'

    callStore.setIncomingCall(
      callIntent.type,
      characterId,
      characterName,
      characterAvatar,
      callIntent.greeting, // AI发起通话时附带的问候语
    )
    return { shouldReturn: true }
  }

  return { shouldReturn: false }
}

/**
 * 6️⃣ 处理 AI 在通话中的挂断
 * @returns shouldReturn=true 表示调用方应 resolve() 并 return
 */
export async function handleCallHangUp(
  fullText: string,
  detectHangUpIntent: (text: string) => any,
  handleAIHangUp: (reason?: string) => void | Promise<void>,
  ctx: CallHandlerContext,
  parsedHangUpIntent?: { hangUp: boolean, reason?: string },
): Promise<CallHandlerResult> {
  const { callStore } = ctx

  if (!callStore.isCallActive) {
    return { shouldReturn: false }
  }

  // 优先使用 processAIResponse 已解析的 hangUpIntent，避免重复扫描原始文本
  const hangUpIntent = parsedHangUpIntent || detectHangUpIntent(fullText)
  if (hangUpIntent.hangUp) {
    console.info('🤖 AI主动挂断电话')
    await handleAIHangUp(hangUpIntent.reason)
    return { shouldReturn: true }
  }

  return { shouldReturn: false }
}

/**
 * 7️⃣ 处理 AI 在通话中的正常回复（添加到转录）
 */
export function handleCallTranscript(
  parsedText: string,
  ctx: CallHandlerContext,
): void {
  const { callStore } = ctx

  if (!callStore.isCallActive || !parsedText)
    return

  callStore.addToTranscript({
    role: 'assistant',
    content: parsedText,
    timestamp: Date.now(),
  })
}

/**
 * 8️⃣ speak_in_call 工具：AI 在通话中说话（native 工具路径，替代 JSON {dialogue}）
 * 与 handleCallTranscript 行为对齐：仅存 dialogue 进转录 + 触发 TTS。
 * description（视频肢体语言）不进转录，与现状一致。
 */
export function handleSpeakInCall(dialogue: string, ctx: CallHandlerContext): void {
  const { callStore } = ctx
  if (!callStore.isCallActive || !dialogue)
    return
  callStore.addToTranscript({ role: 'assistant', content: dialogue, timestamp: Date.now() })
  void ctx.triggerFullTextSpeech?.(dialogue)
}

/**
 * 9️⃣ answer_call 工具：AI 接听/拒绝来电（native 工具路径，替代 handleCallDecision 的 JSON 解析）
 * accept 行为逐条对齐 handleCallDecision 的 accept 分支（chat-call-handler.ts:54-116）。
 */
export function handleAnswerCall(
  decision: 'accept' | 'reject',
  message: string,
  ctx: CallHandlerContext,
): CallHandlerResult {
  const { callStore } = ctx
  if (!callStore.outgoingCallPending)
    return { shouldReturn: false }

  if (decision === 'accept') {
    const pendingCallType = callStore.callType || 'voice'
    const pendingCharacterId = callStore.callCharacterId || 'unknown'
    const pendingCharacterName = callStore.callCharacterName || 'AI'
    const pendingCharacterAvatar = callStore.callCharacterAvatar || undefined

    callStore.cancelOutgoingCall()
    callStore.startCall(pendingCallType, pendingCharacterId, pendingCharacterName, pendingCharacterAvatar, 'user')

    if (message) {
      dispatchCallGreeting(message, pendingCallType, ctx)
    }
    else {
      // 兜底：answer_call 是终止工具，执行后循环即止、AI 不会再续说。若模型把 message 传成
      // 空串（Schema 标 required 但语义为空），通话会静默接通无人开口。此处逐条对齐
      // handleCallDecision 的无问候语兜底（chat-call-handler.ts:88-113）：补一次请求让 AI 开场。
      console.info('📞 [answer_call] 通话已接通，AI 未提供问候语，发送请求获取开场白')
      setTimeout(async () => {
        const callTypeText = pendingCallType === 'voice' ? '语音' : '视频'
        ctx.messages.value.push({
          role: 'system',
          content: `[系统通知：你刚刚接通了用户的${callTypeText}通话请求。现在通话已建立，你应该先开口说第一句话。记住：你现在处于${callTypeText}通话中，请用 speak_in_call 工具自然地打个招呼，不要过于正式。]`,
          timestamp: Date.now(),
          metadata: { type: 'call_system_prompt' },
        } as any)
        ctx.notifyMessagesChanged()

        try {
          await ctx.send('', {
            providerId: ctx.sendOptions.providerId,
            model: ctx.sendOptions.model,
            chatProvider: ctx.sendOptions.chatProvider,
            providerConfig: ctx.sendOptions.providerConfig,
            skipUserMessage: true,
          } as any)
        }
        catch (error) {
          console.error('AI开场白失败:', error)
        }
      }, 500)
    }
    return { shouldReturn: true }
  }

  // 拒接工具是终止工具，不能依赖后续文本流展示拒绝理由。
  callStore.cancelOutgoingCall()
  if (message.trim()) {
    ctx.messages.value.push(createAssistantMessage(message.trim()))
    ctx.notifyMessagesChanged()
  }
  return { shouldReturn: true }
}
